import { serialize } from '../../utils/kql';
import {
	createRow,
	makeValueNode,
	parseForBuilder,
	rawValueOf,
	rowsToText,
} from '../../utils/kqlBuilder';
import { normalizeId } from '../../utils/ids';
import { entryConditionInside } from '../../utils/entryRules';
// The zone a schedule defaults to, already written once for the generator
// editor: a threshold rung is a clock like any other, and two answers to
// "what zone am I in?" is one too many.
import { browserTimezone } from '../collections/ScheduleFields';

/**
 * The notification editor's pure model: the stored row in, a draft out, and
 * the draft back into the JSON THE API VALIDATES — plus the plain-words
 * read-back every panel prints under its controls.
 *
 * ⚠ THE API IS THE AUTHORITY ON THAT JSON, not this file and not the plan.
 * The shape below is read off `keepr-api/utils/notifications/defs.js`
 * (`validatePayload`), the Joi schema in `routes/notificationDefRoutes.js` and
 * the `notification` kind's table in the API's `docs/DATA-MODEL.md`. Three
 * names in particular are not the ones an earlier draft of this editor used:
 * the words a person reads live in `template: {title, body}`, what closes an
 * alert lives in `resolution`, and `category` is REQUIRED on a create.
 *
 * Two rules run through all of it.
 *
 * **Round-trip everything.** The editor renders maybe two thirds of a stored
 * notification. `description`, `severity`, `channels`, `managed`,
 * `forkedFrom`, `resolution.expiresAfterMinutes`, a `resolution.kind` this
 * release has never heard of, a ladder step carrying a key from a later one —
 * every last key rides through a save untouched. The mechanism is
 * `AutomationRuleEditor`'s, character for character: a `KNOWN_*_KEYS` list per
 * object, `omitKeys` to lift the rest off the stored row, and the editor's own
 * keys rebuilt on top so they win. There is not a second mechanism here, and
 * there must never be.
 *
 * **The sentence is one model, three shapes.** *When something happens* and
 * *when an element changes* are both `trigger.type: 'item-event'` — the second
 * is the first with `elementsChanged` and an edge-triggered equality — and
 * *when a date approaches* is `trigger.type: 'threshold'` — or, when the dates
 * it watches are a repeating group's COLUMNS (KPR-836), `'entry-threshold'`:
 * each entry's date its own ladder (`entryGroup` names the group; the columns
 * ride in `dateElements`, the draft's one list of watched dates). Keeping them
 * one draft is what lets a person switch shape without losing the card they
 * already picked.
 *
 * Pure: no React, no antd, no HTTP.
 */

export const SHAPES = { HAPPENS: 'happens', CHANGES: 'changes', APPROACHES: 'approaches' };

export const SHAPE_OPTIONS = [
	{ value: SHAPES.HAPPENS, label: 'When something happens' },
	{ value: SHAPES.CHANGES, label: 'When an element changes' },
	{ value: SHAPES.APPROACHES, label: 'When a date approaches' },
];

/** The three tiers a definition can live at. `global` renders for platform admins only. */
export const SCOPES = { COLLECTION: 'collection', USER: 'user', GLOBAL: 'global' };

export const TIER_OPTIONS = [
	{ value: SCOPES.COLLECTION, label: 'Everyone in this collection' },
	{ value: SCOPES.USER, label: 'Just me' },
	{ value: SCOPES.GLOBAL, label: 'Everyone on keepr' },
];

/** The verb slot of the *happens* sentence. One event each: "is created" is not "is created or updated". */
export const EVENT_VERBS = [
	{ value: 'created', label: 'is created' },
	{ value: 'updated', label: 'is updated' },
	{ value: 'deleted', label: 'is deleted' },
];

/**
 * WHAT KIND of notification this is — the registry the catalogue groups by,
 * and `CATEGORIES` in the API's `utils/notifications/defs.js` verbatim. It is
 * required on a create, so the draft always carries one: `items` is the
 * default because the common notification is about a record.
 */
export const CATEGORIES = ['deadlines', 'items', 'assignments', 'access', 'automations'];

export const DEFAULT_CATEGORY = 'items';

export const CATEGORY_OPTIONS = [
	{ value: 'deadlines', label: 'Deadlines' },
	{ value: 'items', label: 'Records' },
	{ value: 'assignments', label: 'Assignments' },
	{ value: 'access', label: 'Access' },
	{ value: 'automations', label: 'Automations' },
];

/**
 * What closes an open alert. The four the editor builds map onto the API's own
 * `resolution.kind` registry (`utils/notifications/openAlerts.js` KINDS); a
 * stored kind outside this list — `item-updated`, `manual`, or something a
 * later release mints — is carried through verbatim and said out loud rather
 * than rewritten.
 */
export const RESOLVE_KINDS = {
	NONE: 'none',
	RESPONSE: 'response',
	QUERY_ITEM_CREATED: 'query-item-created',
	ELEMENT_POPULATED: 'element-populated',
	// KPR-874: a column of the entry a reminder about entries is about.
	ENTRY_POPULATED: 'entry-populated',
};

export const RESOLVE_OPTIONS = [
	{ value: RESOLVE_KINDS.NONE, label: 'Nothing — it is sent and done' },
	{ value: RESOLVE_KINDS.RESPONSE, label: 'Someone answers it' },
	{ value: RESOLVE_KINDS.QUERY_ITEM_CREATED, label: 'An item is created matching…' },
	{ value: RESOLVE_KINDS.ELEMENT_POPULATED, label: 'An element is filled in' },
];

/**
 * What can close THIS draft. A reminder that watches a date in each entry of a
 * repeating group (KPR-874) can also wait on a column of that entry — offered
 * only then, because only then is there an entry to read. `groupLabel` names
 * the group in the option, as the trigger sentence names it.
 */
export function resolveOptionsFor(draft, { groupLabel } = {}) {
	if (!(draft?.shape === SHAPES.APPROACHES && draft?.entryGroup)) {
		// Kept drawable while it is still the draft's pick (the dates were just
		// moved off the group): the read-back and the save say what to do.
		return draft?.resolve?.kind === RESOLVE_KINDS.ENTRY_POPULATED
			? [...RESOLVE_OPTIONS, { value: RESOLVE_KINDS.ENTRY_POPULATED, label: 'A column of the entry is filled in' }]
			: RESOLVE_OPTIONS;
	}
	const group = groupLabel || draft.entryGroup;
	return [
		...RESOLVE_OPTIONS,
		{ value: RESOLVE_KINDS.ENTRY_POPULATED, label: `A column of ${group} is filled in on that entry` },
	];
}

/** Who may close an open alert by hand. */
export const MANUAL_RESOLVE_OPTIONS = [
	{ value: 'recipient', label: 'Anyone who was notified' },
	{ value: 'item-write', label: 'Anyone who can edit the record' },
	{ value: 'collection-manage', label: 'Managers only' },
	{ value: 'none', label: 'Nobody — only the criterion closes it' },
];

/** What a rung does to the audience it inherited. */
export const AUDIENCE_MODES = [
	{ value: 'inherit', label: 'The same people' },
	{ value: 'expand', label: 'Those people, plus…' },
	{ value: 'replace', label: 'Somebody else instead' },
];

/**
 * The buttons a notification can carry, as this editor authors them.
 *
 * ⚠ `answer` IS ONE OF THEM. It was missing here while both surfaces that draw
 * a response already implemented it — the drawer's textarea flow exists only to
 * serve it (`InboxRow`), the respond routes take its `text`, and the API's own
 * `KINDS` registry marks it performable — so the one free-text answer the
 * platform has could be authored through the API and then rendered read-only by
 * the editor that should have offered it ("a answer button this editor does not
 * draw").
 *
 * The two navigational kinds are here because the API accepts them and stored
 * rows carry them; ⚠ **nothing renders them yet**. Email keeps only the
 * performable four out of `responses` and builds its navigation from `ctas`,
 * and the inbox filters by the same four — so a `create-item` button an author
 * writes here is saved, frozen onto every alert, and drawn by nobody. Closing
 * that is the API's half (folding non-performable responses into the envelope's
 * `ctas` where it is assembled), not a list this editor can shorten: a
 * definition has no `ctas[]` to move them to, and dropping them would silently
 * discard somebody's stored button.
 */
export const RESPONSE_KINDS = [
	{ value: 'acknowledge', label: 'Acknowledge' },
	{ value: 'resolve', label: 'Resolve' },
	{ value: 'snooze', label: 'Snooze' },
	{ value: 'answer', label: 'Answer in words' },
	{ value: 'open-item', label: 'Open the record' },
	{ value: 'create-item', label: 'Create an item' },
];

/**
 * ⚠ THREE, BECAUSE THE API REFUSES A FOURTH. `utils/notifications/responses.js`
 * caps `responses[]` at 3 on the authoring path and answers 400 with a sentence
 * ("a notification with more answers than that is a form, and a form belongs on
 * a record"); the API's docs/DATA-MODEL.md says the same, and the inbox draws at
 * most three. This editor said 5, so an author could add a fourth button, see
 * no objection anywhere, press Save and lose the round trip to a limit the
 * panel could have named.
 */
export const MAX_RESPONSES = 3;

export const RESPONSE_STYLES = [
	{ value: 'primary', label: 'Primary' },
	{ value: 'default', label: 'Normal' },
	{ value: 'danger', label: 'Danger' },
];

/** The ladder's shape rules, enforced here and named in the panel's hint. */
export const MAX_STEPS = 5;
export const MIN_STEP_GAP_MINUTES = 5;
/** Thresholds: strictly descending, 1..8 rungs, each 0..3650 days out. */
export const MAX_OFFSETS = 8;
export const MAX_OFFSET_DAYS = 3650;
/** How many date elements one threshold may watch (registration AND insurance). */
export const MAX_DATE_ELEMENTS = 4;

/**
 * The two stored date triggers (KPR-836): the item's own dates, and a date
 * column of each entry of a repeating group.
 */
export const THRESHOLD = 'threshold';
export const ENTRY_THRESHOLD = 'entry-threshold';

/** What a reminder sends by default: one notice per item for its own dates, one per entry's date for a group's. */
export const defaultCoalesce = entryGroup => ( entryGroup ? 'date' : 'item' );

/** The channel keys the step bank operates. Anything else on a stored step rides through. */
export const BANK_CHANNELS = ['email', 'sms', 'push'];

/**
 * The audience types THIS EDITOR DRAWS — `NotifyAudienceFields`' own four, plus
 * the legacy single-`group` shape it seeds into the multi picker.
 *
 * ⚠ IT IS NOT THE PLATFORM'S LIST. The API's registry
 * (`keepr-api utils/automations/audience.js` RESOLVERS) also carries
 * `element-path` — *"whoever is in `manager` on the record's `assignee`"*, the
 * one expansion type, dot-walking at most two lookups — and a later release may
 * add more. An audience whose type is not on THIS list is carried through a
 * save byte for byte and said out loud, exactly as an unknown `resolution.kind`
 * and an unknown response kind already are.
 *
 * The alternative is what this file used to do: coerce the unrecognised type to
 * `{type:'role', minRole:'read'}` and then SAVE that, so opening somebody's
 * "tell the manager" notification and pressing Save quietly turned one person
 * into every reader of the collection. A widening nobody was told about is the
 * worst thing a settings surface can do, and it must not be possible to do it
 * by accident.
 */
export const EDITOR_AUDIENCE_TYPES = ['groups', 'group', 'users', 'role', 'element'];

export { browserTimezone };

let uidCounter = 0;
const uid = () => `n${++uidCounter}`;

const omitKeys = (obj, keys) => Object.fromEntries(
	Object.entries(obj || {}).filter(([key]) => !keys.includes(key))
);

const trim = text => (typeof text === 'string' ? text.trim() : '');

/* ── The identity ────────────────────────────────────────────────────────── */

/**
 * `key` is the catalog identity the three tiers SHARE (Decision 2), so it is
 * derived from the name only when there is nothing to inherit: an override
 * seeded from `?from=<key>` keeps the key it is overriding, which is the whole
 * mechanism by which it IS the override.
 */
export function slugifyKey(name, fallback = 'notification') {
	const slug = String(name || '')
		.toLowerCase()
		.normalize('NFKD')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 48)
		.replace(/-+$/g, '');
	// The API's own pattern refuses a leading hyphen and a one-character key.
	return slug.length >= 2 ? slug : fallback;
}

/* ── KQL: the becomes clause, and the condition beside it ────────────────── */

/** The element field descriptor out of a built vocabulary, or null. */
export const findElementField = (fields, name) => (fields || []).find(
	field => !field.facet && !field.system && (field.path || []).length === 1 && field.path[0] === name
) || null;

/**
 * "`Assignee` becomes me" as one canonical KQL condition, built through the
 * builder's own machinery rather than by string concatenation — `me` has to
 * come out as the grammar's keyword node and not the text "me", and a choice
 * value has to be quoted the way the parser reads it back.
 */
export function becomesKql(field, raw) {
	if (!field || raw === undefined || raw === null || raw === '') return '';
	const value = makeValueNode(field.dataType, 'eq', raw);
	if (!value) return '';
	return rowsToText([{ ...createRow(field), cmp: 'eq', value }]);
}

/** Two canonical queries as one. Either half empty is the other half. */
export function andKql(left, right) {
	const parts = [trim(left), trim(right)].filter(Boolean);
	if (parts.length < 2) return parts[0] || '';
	const asts = parts.map(text => parseForBuilder(text).ast);
	// Both halves are the builder's own output in practice; a hand-typed
	// advanced query that will not parse is still ANDed, parenthesised so an
	// `or` inside it cannot swallow the clause beside it.
	if (asts.every(Boolean)) return serialize({ op: 'and', args: asts });
	return parts.map(part => `(${part})`).join(' and ');
}

/**
 * Pull the becomes clause back out of a stored condition: the first `=` row on
 * the element that changed. What is left is the optional condition beside it.
 * A query the builder cannot decompose keeps its whole text as the condition
 * and leaves the slot empty — never silently rewritten.
 */
export function splitBecomes(kql, elementName) {
	const text = trim(kql);
	if (!text || !elementName) return { raw: undefined, rest: text };
	const { rows } = parseForBuilder(text);
	if (!Array.isArray(rows)) return { raw: undefined, rest: text };
	const index = rows.findIndex(row => row.cmp === 'eq' && (row.path || []).length === 1 && row.path[0] === elementName);
	if (index < 0) return { raw: undefined, rest: text };
	const rest = rows.filter((row, i) => i !== index);
	return { raw: rawValueOf(rows[index].value), rest: rowsToText(rest) };
}

/* ── Drafts ──────────────────────────────────────────────────────────────── */

const emptyNotify = () => ({ audienceType: 'role', groupIds: [], userIds: [], minRole: 'read', element: '', unknown: null });

export function emptyDraft({ scope = SCOPES.COLLECTION, shape = SHAPES.HAPPENS, cardId = '', key = '' } = {}) {
	return {
		key,
		name: '',
		category: DEFAULT_CATEGORY,
		scope,
		shape,
		cardId: cardId || '',
		events: ['created'],
		changedElement: '',
		becomes: undefined,
		dateElements: [],
		// The repeating group whose entries' dates are watched (KPR-836), or ''
		// when the dates are the item's own.
		entryGroup: '',
		direction: 'before',
		offsetsDays: [60],
		coalesce: 'item',
		at: '09:00',
		timezone: browserTimezone(),
		conditionKql: '',
		// Which entries are watched: the inside of the stored block on the group.
		entryConditionKql: '',
		notify: emptyNotify(),
		title: '',
		body: '',
		resolve: {
			kind: RESOLVE_KINDS.NONE,
			cardId: '',
			element: '',
			// KPR-874: the column of the watched entry (entry-populated).
			column: '',
			kql: '',
			bindOn: [],
			allowManualResolve: 'recipient',
			unknown: null,
		},
		steps: [],
		responses: [],
		raw: null,
	};
}

const shapeOf = trigger => {
	if (trigger?.type === THRESHOLD || trigger?.type === ENTRY_THRESHOLD) return SHAPES.APPROACHES;
	return (Array.isArray(trigger?.elementsChanged) && trigger.elementsChanged.length)
		? SHAPES.CHANGES
		: SHAPES.HAPPENS;
};

/**
 * A rung names its people in a LIST, under the key its mode chooses:
 * `{mode:'expand', add:[spec]}` or `{mode:'replace', to:[spec]}` — never a
 * spec spread flat beside the mode (`defs.js validateStepAudience`, which
 * refuses anything else with a sentence). The bank edits the FIRST spec; the
 * rest of the list rides through `stepAudienceTail`.
 */
const stepAudienceSpecs = audience => {
	const list = audience?.mode === 'replace' ? audience.to : audience?.add;
	return Array.isArray(list) ? list : [];
};

const stepFromStored = step => {
	const audience = step?.audience || {};
	const channels = Array.isArray(step?.channels) ? step.channels : [];
	const specs = stepAudienceSpecs(audience);
	return {
		uid: uid(),
		afterMinutes: Number.isFinite(Number(step?.afterMinutes)) ? Number(step.afterMinutes) : MIN_STEP_GAP_MINUTES,
		// Only the bank's three are operable; anything a later release adds
		// rides in `raw` and is put back on save.
		channels: channels.filter(key => BANK_CHANNELS.includes(key)),
		audienceMode: ['inherit', 'expand', 'replace'].includes(audience.mode) ? audience.mode : 'inherit',
		notify: audienceFromStored(specs[0]),
		// A rung may name up to four audiences; this editor draws one. The
		// others are nobody's to drop.
		audienceTail: specs.slice(1),
		title: step?.title || '',
		body: step?.body || '',
		raw: step || null,
	};
};

const responseFromStored = response => {
	const known = RESPONSE_KINDS.some(entry => entry.value === response?.kind);
	return {
		uid: uid(),
		key: response?.key || '',
		label: response?.label || '',
		kind: response?.kind || 'acknowledge',
		minutes: Number.isFinite(Number(response?.minutes)) ? Number(response.minutes) : null,
		cardId: normalizeId(response?.card_id) || '',
		style: response?.style || 'default',
		// A kind this release does not build renders read-only and is emitted
		// exactly as it came in: somebody's stored button is not ours to drop.
		unknown: known ? null : (response || null),
		raw: response || null,
	};
};

/**
 * The audience half of a `notify` draft, read off a stored audience object.
 * `NotifyActionFields.notifyDraftFromAction` is the canonical reader and this
 * hands it the shape it expects — one legacy `{type:'group'}` seeds the multi
 * picker there, so a rule authored before groups went multi is still editable.
 *
 * ⚠ A TYPE THIS EDITOR DOES NOT DRAW IS CARRIED, NEVER COERCED. See
 * `EDITOR_AUDIENCE_TYPES`: `element-path` — and whatever the API's registry
 * grows next — rides out in `unknown` and goes back exactly as it came, because
 * the alternative silently widens one person to a whole collection.
 *
 * An ABSENT audience is not unknown, it is the default: the resolver has always
 * read a missing audience as "everyone who can read it", which is the role
 * shape this seeds.
 */
function audienceFromStored(audience) {
	const source = audience || {};
	const groupIds = source.type === 'group'
		? [source.group_id]
		: (Array.isArray(source.group_ids) ? source.group_ids : []);
	const drawn = !source.type || EDITOR_AUDIENCE_TYPES.includes(source.type);
	return {
		audienceType: source.type === 'group' ? 'groups' : (drawn && source.type ? source.type : 'role'),
		groupIds: groupIds.map(normalizeId).filter(Boolean),
		userIds: (Array.isArray(source.userIds) ? source.userIds : []).map(normalizeId).filter(Boolean),
		minRole: source.minRole || 'read',
		element: typeof source.element === 'string' ? source.element : '',
		unknown: drawn ? null : audience,
	};
}

/** One stored notification → the editor's draft. `fromKey` seeds a new override. */
export function draftFromStored(stored, { fromKey = '', scope, shape, cardId } = {}) {
	if (!stored) {
		return emptyDraft({
			scope: scope || SCOPES.COLLECTION,
			shape: shape || SHAPES.HAPPENS,
			cardId: cardId || '',
			key: fromKey || '',
		});
	}

	const trigger = stored.trigger || {};
	const condition = trigger.condition || {};
	const draftShape = shapeOf(trigger);
	const changedElement = draftShape === SHAPES.CHANGES ? trigger.elementsChanged[0] : '';
	const split = draftShape === SHAPES.CHANGES
		? splitBecomes(condition.kql, changedElement)
		: { raw: undefined, rest: condition.kql || '' };

	// `resolution`, not `resolve`: the API stores and serves it under that name
	// (defs.js `serializeDef`), and the editor reads what is actually there.
	const resolve = stored.resolution || {};
	const knownKind = Object.values(RESOLVE_KINDS).includes(resolve.kind);
	const template = stored.template || {};
	// A reminder about each entry's date (KPR-836): its group, its columns in
	// the one list of watched dates, its own default fold.
	const entryGroup = trigger.type === ENTRY_THRESHOLD && typeof trigger.group === 'string' ? trigger.group : '';
	const watchedDates = entryGroup ? trigger.dateColumns : trigger.dateElements;
	const coalesce = trigger.coalesce === 'date' || trigger.coalesce === 'item' ? trigger.coalesce : defaultCoalesce(entryGroup);

	return {
		key: stored.key || fromKey || '',
		name: stored.name || '',
		category: CATEGORIES.includes(stored.category) ? stored.category : DEFAULT_CATEGORY,
		scope: [SCOPES.COLLECTION, SCOPES.USER, SCOPES.GLOBAL].includes(stored.scope) ? stored.scope : SCOPES.COLLECTION,
		shape: draftShape,
		cardId: normalizeId(trigger.card_id) || '',
		events: Array.isArray(trigger.events) && trigger.events.length ? trigger.events : ['created'],
		changedElement,
		becomes: split.raw,
		dateElements: Array.isArray(watchedDates) ? watchedDates : [],
		entryGroup,
		direction: trigger.direction === 'after' ? 'after' : 'before',
		offsetsDays: Array.isArray(trigger.offsetsDays) && trigger.offsetsDays.length ? trigger.offsetsDays : [60],
		coalesce,
		at: trigger.at || '09:00',
		timezone: trigger.timezone || browserTimezone(),
		conditionKql: split.rest || '',
		entryConditionKql: entryGroup ? entryConditionInside(trigger.entryCondition?.kql) : '',
		notify: audienceFromStored(stored.audience),
		// The words live in `template`, never at the top level.
		title: template.title || '',
		body: template.body || '',
		resolve: {
			kind: knownKind ? resolve.kind : RESOLVE_KINDS.NONE,
			cardId: normalizeId(resolve.card_id) || '',
			element: resolve.element || '',
			column: resolve.column || '',
			kql: resolve.condition?.kql || '',
			bindOn: Array.isArray(resolve.bindOn) ? resolve.bindOn : [],
			allowManualResolve: resolve.allowManualResolve || 'recipient',
			unknown: resolve.kind && !knownKind ? resolve : null,
		},
		steps: (Array.isArray(stored.escalation?.steps) ? stored.escalation.steps : []).map(stepFromStored),
		responses: (Array.isArray(stored.responses) ? stored.responses : []).map(responseFromStored),
		raw: stored,
	};
}

/* ── The payload ─────────────────────────────────────────────────────────── */

// The keys this editor OWNS. Everything else on the stored row — description,
// severity, enabled, channels, forkedFrom, and whatever a later release adds —
// is lifted off and put back untouched.
const KNOWN_TOP_KEYS = [
	'name', 'category', 'scope', 'collection_id', 'userId', 'availability',
	'trigger', 'audience', 'template', 'resolution', 'escalation', 'responses',
];
// Server-owned columns that never travel in a write body at all. `managed` and
// `nextRunAt` are on the list for the same reason `rev` is: `serializeDef`
// sends them down on every read and the API decides them, so echoing one back
// is at best noise and at worst a client stating a fact it does not own.
const SERVER_KEYS = [
	'_id', 'kind', 'status', 'rev', 'managed', 'nextRunAt',
	'createdAt', 'updatedAt', 'createdBy', 'updatedBy', '__v',
];
const KNOWN_TRIGGER_KEYS = [
	'type', 'card_id', 'events', 'elementsChanged', 'condition',
	'dateElements', 'direction', 'offsetsDays', 'coalesce', 'at', 'timezone',
	// A reminder about each entry's date (KPR-836).
	'group', 'dateColumns', 'entryCondition',
];
const KNOWN_CONDITION_KEYS = ['kql', 'evaluateOn'];
const KNOWN_TEMPLATE_KEYS = ['title', 'body'];
// `bindings` is the authored half and `bindOn` is the API's own derivation
// from it (`openAlerts.validateResolution`), so both are the editor's to
// rebuild — see `buildResolve`.
// `group` and `column` (KPR-874) are the entry criterion's; the group is the
// trigger's, so it is rebuilt from the draft rather than carried.
const KNOWN_RESOLVE_KEYS = ['kind', 'card_id', 'element', 'condition', 'bindings', 'bindOn', 'allowManualResolve', 'group', 'column'];
const KNOWN_STEP_KEYS = ['afterMinutes', 'channels', 'audience', 'title', 'body'];
const KNOWN_RESPONSE_KEYS = ['key', 'label', 'kind', 'minutes', 'card_id', 'style'];
const KNOWN_ESCALATION_KEYS = ['steps'];

const buildAudience = draft => {
	// A type the editor never rendered goes back exactly as it arrived — the
	// `buildResolve`/`buildResponse` rule, applied to the one object where
	// getting it wrong widens who gets told (see `EDITOR_AUDIENCE_TYPES`).
	if (draft?.unknown) return { ...draft.unknown };
	if (draft?.audienceType === 'groups') return { type: 'groups', group_ids: draft.groupIds || [] };
	if (draft?.audienceType === 'users') return { type: 'users', userIds: draft.userIds || [] };
	if (draft?.audienceType === 'element') return { type: 'element', element: draft.element || '' };
	return { type: 'role', minRole: draft?.minRole || 'read' };
};

const buildTrigger = (draft, field) => {
	const stored = draft.raw?.trigger || null;
	const extras = omitKeys(stored, KNOWN_TRIGGER_KEYS);
	const conditionExtras = omitKeys(stored?.condition, KNOWN_CONDITION_KEYS);

	if (draft.shape === SHAPES.APPROACHES) {
		// The dates are a repeating group's columns (KPR-836): each entry's own
		// ladder, under its own trigger type — which a release before it never
		// runs — and the entry's narrowing beside the item's.
		const entryGroup = draft.entryGroup || '';
		const trigger = {
			...extras,
			type: entryGroup ? ENTRY_THRESHOLD : THRESHOLD,
			card_id: draft.cardId,
			...(entryGroup
				? { group: entryGroup, dateColumns: draft.dateElements || [] }
				: { dateElements: draft.dateElements || [] }),
			direction: draft.direction || 'before',
			offsetsDays: draft.offsetsDays || [],
			coalesce: draft.coalesce || defaultCoalesce(entryGroup),
			at: draft.at || '09:00',
			timezone: draft.timezone || browserTimezone(),
		};
		const kql = trim(draft.conditionKql);
		if (kql || Object.keys(conditionExtras).length) trigger.condition = { ...conditionExtras, ...(kql ? { kql } : {}) };
		const entryKql = entryGroup ? trim(draft.entryConditionKql) : '';
		if (entryKql) trigger.entryCondition = { kql: entryKql };
		return trigger;
	}

	const trigger = { ...extras, type: 'item-event', card_id: draft.cardId };

	if (draft.shape === SHAPES.CHANGES) {
		// "Becomes" IS the edge, so `evaluateOn: 'transition'` is written out
		// rather than left to the API's default: the word in the control and
		// the value on the wire have to be the same fact, and a default that
		// moves must not quietly change what somebody authored.
		trigger.events = ['updated'];
		trigger.elementsChanged = [draft.changedElement];
		const kql = andKql(becomesKql(field, draft.becomes), draft.conditionKql);
		trigger.condition = { ...conditionExtras, ...(kql ? { kql } : {}), evaluateOn: 'transition' };
		return trigger;
	}

	trigger.events = draft.events?.length ? draft.events : ['created'];
	const kql = trim(draft.conditionKql);
	if (kql || Object.keys(conditionExtras).length) {
		// `evaluateOn` has no control on this shape; a stored 'match' rides
		// through in the extras rather than being reset to the default.
		trigger.condition = { ...conditionExtras, ...(kql ? { kql } : {}) };
	}
	return trigger;
};

/**
 * `resolution` — what keeps the notification open and what closes it.
 *
 * ⚠ THE BINDINGS ARE THE AUTHORED HALF, `bindOn` IS NOT. The API takes
 * `bindings: {<element on the watched card>: '{{trigger.<field>}}'}`, freezes
 * each value onto the alert when it opens, and DERIVES `bindOn` as the sorted
 * key list (`openAlerts.validateResolution`). The panel asks the question in
 * its "For the same…" list of names, which is the same-named token on both
 * sides; a stored binding that maps one name onto another is that author's,
 * not ours, so it is kept for every name still chosen.
 */
const buildResolve = draft => {
	const resolve = draft.resolve || {};
	// A kind the editor never rendered goes back exactly as it arrived.
	if (resolve.unknown) return { ...resolve.unknown };

	const extras = omitKeys(draft.raw?.resolution, KNOWN_RESOLVE_KEYS);
	const out = { ...extras, kind: resolve.kind || RESOLVE_KINDS.NONE };
	if (resolve.kind === RESOLVE_KINDS.NONE) return out;

	out.allowManualResolve = resolve.allowManualResolve || 'recipient';
	if (resolve.kind === RESOLVE_KINDS.RESPONSE) return out;

	// A column of the watched entry (KPR-874): the group is the trigger's, and
	// there is no card to pick — the entry is on the record that fired it.
	if (resolve.kind === RESOLVE_KINDS.ENTRY_POPULATED) {
		if (draft.entryGroup) out.group = draft.entryGroup;
		if (resolve.column) out.column = resolve.column;
		if (trim(resolve.kql)) out.condition = { kql: trim(resolve.kql) };
		return out;
	}

	if (resolve.cardId) out.card_id = resolve.cardId;
	if (resolve.kind === RESOLVE_KINDS.ELEMENT_POPULATED && resolve.element) out.element = resolve.element;
	if (trim(resolve.kql)) out.condition = { kql: trim(resolve.kql) };
	if (resolve.bindOn?.length) {
		const stored = draft.raw?.resolution?.bindings || {};
		out.bindings = Object.fromEntries(
			resolve.bindOn.map(name => [name, stored[name] || `{{trigger.${name}}}`])
		);
	}
	return out;
};

/**
 * ⚠ `personal` IS INVARIANT 2 ON THE RUNGS, and it is not the editor being
 * tidy. A ladder is an audience with a delay on it, so a rung free to replace
 * its audience would hand the whole collection a notification the personal
 * tier exists to keep to one person. The API refuses any mode but `inherit` at
 * `scope: 'user'` (400 `notification_user_tier_audience`), refuses it again
 * against the stored row on both fire paths, and reads an older alert's rung
 * back as `inherit` whatever it says — so `inherit` is what that rung MEANS,
 * and writing anything else is a save that cannot land. The panel does not
 * draw the control at this tier; this is the same fact on the wire, so a draft
 * that carried a mode over from another tier cannot leave a 400 behind it.
 */
const buildStep = (step, { personal = false } = {}) => {
	const extras = omitKeys(step.raw, KNOWN_STEP_KEYS);
	const carried = (Array.isArray(step.raw?.channels) ? step.raw.channels : [])
		.filter(key => !BANK_CHANNELS.includes(key));
	const mode = personal ? 'inherit' : step.audienceMode;
	const out = {
		...extras,
		afterMinutes: step.afterMinutes,
		// The carried members first, in the order the row stored them: a channel
		// a later release adds is not one of the bank's three, and is nobody's
		// to drop. (`inApp` is not among them at either level — the inbox is not
		// one of the toggles, and naming it here is a 400.)
		channels: [...carried, ...(step.channels || [])],
		// The specs go in a LIST under the key the mode chooses — `add` widens,
		// `to` hands the rung over — and a spec spread flat beside the mode is a
		// 400 with a sentence (`defs.js validateStepAudience`).
		audience: mode === 'inherit'
			? { mode: 'inherit' }
			: {
				mode,
				[mode === 'replace' ? 'to' : 'add']: [
					buildAudience(step.notify),
					...(step.audienceTail || []),
				],
			},
	};
	if (trim(step.title)) out.title = trim(step.title);
	if (trim(step.body)) out.body = trim(step.body);
	return out;
};

const buildResponse = response => {
	if (response.unknown) return { ...response.unknown };
	const extras = omitKeys(response.raw, KNOWN_RESPONSE_KEYS);
	const out = {
		...extras,
		key: response.key || slugifyKey(response.label, 'response'),
		label: trim(response.label),
		kind: response.kind,
		style: response.style || 'default',
	};
	if (response.kind === 'snooze' && Number.isFinite(response.minutes)) out.minutes = response.minutes;
	if (response.kind === 'create-item' && response.cardId) out.card_id = response.cardId;
	return out;
};

/**
 * The JSON the API validates (`defs.js validatePayload`). `isNew` decides two
 * things and only two: a create carries the catalog `key` (a PATCH may not —
 * the API holds it immutable), and a create names its tier (Decision 8 forbids
 * promote/demote, so an existing row's scope is not the editor's to move).
 *
 * `field` is the becomes slot's element descriptor, needed to type its value
 * into the grammar; every other shape ignores it.
 */
export function buildPayload(draft, { isNew = true, collectionId = null, field = null } = {}) {
	const extras = omitKeys(draft.raw, [...KNOWN_TOP_KEYS, ...SERVER_KEYS, 'key']);
	const personal = draft.scope === SCOPES.USER;
	const payload = {
		...extras,
		name: trim(draft.name),
		// Required on a create, and one of the two registries the catalogue
		// renders by — so it is always stated rather than left to a default
		// this editor cannot see.
		category: CATEGORIES.includes(draft.category) ? draft.category : DEFAULT_CATEGORY,
		trigger: buildTrigger(draft, field),
		// The words a person reads live under `template`, never at the top
		// level — `{{trigger.*}}` tokens and all.
		template: {
			...omitKeys(draft.raw?.template, KNOWN_TEMPLATE_KEYS),
			title: trim(draft.title),
			body: trim(draft.body),
		},
		resolution: buildResolve(draft),
		escalation: {
			...omitKeys(draft.raw?.escalation, KNOWN_ESCALATION_KEYS),
			steps: (draft.steps || []).map(step => buildStep(step, { personal })),
		},
		responses: (draft.responses || []).map(buildResponse),
	};

	// A personal notification reaches exactly one person — its owner — and the
	// API derives that from the token. Sending an audience here would be the
	// editor asking for something the tier structurally refuses (Decision 8).
	if (!personal) payload.audience = buildAudience(draft.notify);

	// ⚠ `availability` IS A GLOBAL-TIER FIELD AND A 400 ANYWHERE ELSE — the API
	// refuses it outright below `global` rather than ignoring it, and
	// `serializeDef` sends it down as an explicit `null` on every collection
	// row. So it is lifted off the extras and put back only where it means
	// something.
	if (draft.scope === SCOPES.GLOBAL && draft.raw?.availability) {
		payload.availability = draft.raw.availability;
	}

	if (isNew) {
		payload.key = draft.key || slugifyKey(draft.name);
		payload.scope = draft.scope;
		// Global has no parent collection; that is the whole reason these
		// routes are flat rather than nested under one.
		if (draft.scope !== SCOPES.GLOBAL && collectionId) payload.collection_id = collectionId;
	}

	return payload;
}

/* ── Validation ──────────────────────────────────────────────────────────── */

/**
 * What this editor can answer WITHOUT the server. The API validates the whole
 * shape at write time and answers with field-level sentences written for
 * humans; re-implementing that here would only produce a second, drifting copy
 * of it. These are the rules the ladder and the rungs carry that a person
 * cannot see a 400 for without losing their typing.
 */
export function validateDraft(draft) {
	if (!trim(draft.name)) return 'Give the notification a name.';
	if (!draft.cardId) return 'Pick the card this watches.';

	if (draft.shape === SHAPES.CHANGES && !draft.changedElement) {
		return 'Pick the element that changes.';
	}
	if (draft.shape === SHAPES.HAPPENS && !(draft.events || []).length) {
		return 'Pick what has to happen — created, updated or deleted.';
	}
	if (draft.shape === SHAPES.APPROACHES) {
		if (!(draft.dateElements || []).length) return 'Pick at least one date to watch.';
		const offsets = draft.offsetsDays || [];
		if (!offsets.length) return 'Add at least one lead time — 60 days out, say.';
		if (offsets.length > MAX_OFFSETS) return `A threshold watches at most ${MAX_OFFSETS} lead times.`;
		if (offsets.some(days => !Number.isFinite(days) || days < 0 || days > MAX_OFFSET_DAYS)) {
			return `Every lead time is a whole number of days between 0 and ${MAX_OFFSET_DAYS}.`;
		}
		for (let i = 1; i < offsets.length; i++) {
			if (offsets[i] >= offsets[i - 1]) return 'Lead times run from the furthest out to the nearest — 60, 30, 20, 10, 5.';
		}
	}

	// KPR-874: a column of the watched entry needs an entry, and a column.
	if (draft.resolve?.kind === RESOLVE_KINDS.ENTRY_POPULATED && !draft.resolve?.unknown) {
		if (!(draft.shape === SHAPES.APPROACHES && draft.entryGroup)) {
			return 'Only a reminder about a date in each entry can stay open until a column of the entry is filled in. Pick such a date, or choose another way for it to close.';
		}
		if (!draft.resolve.column) return 'Pick the column that, filled in on the entry, closes it.';
	}

	if (!trim(draft.title)) return 'Write the line people will read.';

	const steps = draft.steps || [];
	if (steps.length > MAX_STEPS) return `A ladder has at most ${MAX_STEPS} rungs.`;
	for (let i = 0; i < steps.length; i++) {
		const minutes = steps[i].afterMinutes;
		if (!Number.isFinite(minutes) || minutes < MIN_STEP_GAP_MINUTES) {
			return `Rung ${i + 1} fires at least ${MIN_STEP_GAP_MINUTES} minutes after the alert opens.`;
		}
		if (i > 0 && minutes < steps[i - 1].afterMinutes + MIN_STEP_GAP_MINUTES) {
			return `Rung ${i + 1} is less than ${MIN_STEP_GAP_MINUTES} minutes after rung ${i}. Delays are measured from when the alert opened.`;
		}
		if (!steps[i].channels.length && !(steps[i].raw?.channels || []).length) {
			return `Rung ${i + 1} has no way to reach anyone. Turn a channel on, or remove the rung.`;
		}
	}

	const responses = draft.responses || [];
	// The API's cap, checked here for the same reason the ladder's is: a person
	// who opened a definition authored through the API with four buttons should
	// read the limit rather than lose a save to it.
	if (responses.length > MAX_RESPONSES) {
		return `A notification carries at most ${MAX_RESPONSES} buttons. More answers than that is a form, and a form belongs on a record.`;
	}

	const seen = new Set();
	for (const response of responses) {
		if (response.unknown) continue;
		if (!trim(response.label)) return 'Every button needs a label.';
		const key = response.key || slugifyKey(response.label, 'response');
		if (seen.has(key)) return `Two buttons are both called "${trim(response.label)}". Give them different names.`;
		seen.add(key);
	}

	return null;
}

/* ── The read-backs ──────────────────────────────────────────────────────── */

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * The sentence under the slot row, in plain words. Generated, never typed:
 * a trigger that changes cannot leave a stale sentence behind it, which is the
 * same discipline `FilterChips` applies to a query and `describeTrigger` to a
 * stored row.
 *
 * Names come back marked with single asterisks (`*Issue*`), the convention
 * `notificationCopy.splitTerms` already reads, so the surface can print a name
 * in ink inside a muted sentence.
 */
export function describeTriggerDraft(draft, { cardLabel, elementLabel, valueLabel, groupLabel, columnLabel } = {}) {
	const card = cardLabel ? `*${cardLabel}*` : 'a record';

	if (draft.shape === SHAPES.APPROACHES) {
		const entryGroup = draft.entryGroup || '';
		const label = entryGroup ? (name => columnLabel?.(name) || name) : (name => elementLabel?.(name) || name);
		const dates = (draft.dateElements || []).map(name => `*${label(name)}*`);
		if (!dates.length) return 'Pick a date and this says when it fires.';
		const offsets = draft.offsetsDays || [];
		const when = offsets.length === 1
			? plural(offsets[0], 'day')
			: `${offsets.slice(0, -1).join(', ')} and ${offsets[offsets.length - 1]} days`;
		const side = draft.direction === 'after' ? 'after' : 'before';
		if (entryGroup) {
			// KPR-836: each entry's date is its own ladder.
			const group = `*${groupLabel || entryGroup}*`;
			const one = draft.coalesce === 'item' ? 'one notice per item, listing every entry' : 'one notice per entry date';
			return `${when} ${side} ${dates.join(' or ')} of each ${group} entry on ${card}, at ${draft.at || '09:00'} — ${one}.`;
		}
		const one = draft.coalesce === 'date' ? 'one notice per date' : 'one notice per record';
		return `${when} ${side} ${dates.join(' or ')} on ${card}, at ${draft.at || '09:00'} — ${one}.`;
	}

	if (draft.shape === SHAPES.CHANGES) {
		const element = draft.changedElement ? `*${elementLabel?.(draft.changedElement) || draft.changedElement}*` : 'an element';
		const value = valueLabel ? ` to *${valueLabel}*` : '';
		return `When ${element} on ${card} changes${value}. Once, as it becomes true — not every save afterwards.`;
	}

	const verbs = (draft.events || []).map(event => EVENT_VERBS.find(v => v.value === event)?.label || event);
	const verb = verbs.length ? verbs.join(' or ') : 'changes';
	return `When ${card} ${verb}.`;
}

/**
 * An audience this editor does not draw, in plain words — the line that stands
 * in the picker's place rather than a picker cheerfully showing the wrong
 * answer. Marked up the same way every other read-back is, so the names inside
 * it read in ink.
 *
 * `element-path` gets a real sentence because the platform actually has one and
 * its path is readable ("assignee.manager" → *manager* on the record's
 * *assignee*); anything a later release mints is named by its type and left
 * alone. Either way the last clause is the promise: it is kept exactly as it is.
 */
export function describeUnknownAudience(audience) {
	if (!audience) return null;
	const type = trim(audience.type) || 'unrecognised';

	if (type === 'element-path' && trim(audience.path)) {
		const segments = trim(audience.path).split('.').filter(Boolean);
		const terminal = `*${segments[segments.length - 1]}*`;
		const walked = segments.slice(0, -1).map(name => `*${name}*`).join(' → ');
		const who = walked ? `Whoever is in ${terminal} on the record's ${walked}` : `Whoever is in ${terminal}`;
		return `${who}. This editor does not draw that audience, so it is kept exactly as it is.`;
	}

	return `This reaches a *${type}* audience this editor does not draw. It is kept exactly as it is.`;
}

/** What closes it, in plain words. */
export function describeResolveDraft(draft, { cardLabel, elementLabel, groupLabel, columnLabel } = {}) {
	const resolve = draft.resolve || {};
	if (resolve.unknown) {
		return `This one closes on a rule this editor does not draw (${resolve.unknown.kind}). It is kept exactly as it is.`;
	}
	if (resolve.kind === RESOLVE_KINDS.NONE) return 'It is sent and done. Nothing stays open, and nothing escalates.';
	if (resolve.kind === RESOLVE_KINDS.RESPONSE) return 'It stays open until somebody presses one of the buttons below.';
	if (resolve.kind === RESOLVE_KINDS.ENTRY_POPULATED) {
		// KPR-874: "It stays open until Given is filled in on that dose."
		if (!(draft.shape === SHAPES.APPROACHES && draft.entryGroup)) {
			return 'Watch a date of a repeating group\'s entries above, and it can stay open until a column of that entry is filled in.';
		}
		const column = resolve.column ? `*${columnLabel?.(resolve.column) || resolve.column}*` : 'a column';
		const entry = groupLabel || draft.entryGroup;
		return `It stays open until ${column} is filled in on that *${entry}*.`;
	}

	const card = cardLabel ? `*${cardLabel}*` : 'a matching record';
	const bound = resolve.bindOn?.length
		? ` for the same ${resolve.bindOn.map(name => `*${elementLabel?.(name) || name}*`).join(' and ')}`
		: '';
	if (resolve.kind === RESOLVE_KINDS.QUERY_ITEM_CREATED) {
		return `It stays open until ${card} is created${bound}.`;
	}
	const element = resolve.element ? `*${elementLabel?.(resolve.element) || resolve.element}*` : 'an element';
	return `It stays open until ${element} on ${card} is filled in${bound}.`;
}

/** One rung, in plain words. Delays are from the OPENING, never from the rung above. */
export function describeStep(step, { audienceLabel } = {}) {
	const minutes = Number.isFinite(step?.afterMinutes) ? step.afterMinutes : 0;
	const when = minutes >= 60 && minutes % 60 === 0
		? plural(minutes / 60, 'hour')
		: plural(minutes, 'minute');
	const channels = (step?.channels || []).map(key => (key === 'sms' ? 'text' : key)).join(' and ');
	const how = channels ? `by ${channels}` : 'to the inbox only';
	const who = step?.audienceMode === 'inherit'
		? 'the same people'
		: (step?.audienceMode === 'expand'
			? `those people plus ${audienceLabel || 'the ones named here'}`
			: (audienceLabel || 'the people named here'));
	return `${when} after it opens, if it is still open: ${how}, to ${who}.`;
}
